import React, { useState, useEffect } from 'react'
import { useDrag, useDragLayer } from 'react-dnd'
import { User, Shield, Zap, Target, ChevronDown, ChevronRight, Cone, Circle, Square, RectangleHorizontal } from 'lucide-react'
import usePlayStore from '../../stores/playStore'

// Custom drag layer component
const CustomDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }))

  if (!isDragging || !currentOffset || !item) {
    return null
  }

  // Get the appropriate icon based on item type
  const getIcon = (type) => {
  const iconMap = {
    'red-team': Shield,
    'blue-team': User,
    'green-team': Zap,
    'orange-team': Target,
    cone: Cone,
    'soccer-ball': Circle,
  }
    return iconMap[type] || User
  }

  const Icon = getIcon(item.type)
  const isTeam = item.type === 'red-team' || item.type === 'blue-team' || item.type === 'green-team' || item.type === 'orange-team'
  const label = isTeam 
    ? item.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
    : item.type === 'soccer-ball' ? 'Soccer Ball' : 'Cone'

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 1000,
        left: currentOffset.x,
        top: currentOffset.y,
        transform: 'rotate(5deg) scale(1.2)',
        opacity: 0.9,
      }}
    >
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-xl border-2"
        style={{ 
          backgroundColor: item.color,
          borderColor: item.color,
          boxShadow: '0 8px 20px rgba(0,0,0,0.4)'
        }}
      >
        <Icon size={18} />
      </div>
    </div>
  )
}

const DraggableItem = ({ type, icon: Icon, color, label, dragType = 'team' }) => {
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: dragType,
    item: { 
      type, 
      color,
      number: null // Number will be assigned by the store
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [])
  
  // Use a transparent empty image as the drag preview to hide default browser drag image
  useEffect(() => {
    const img = new Image()
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='
    preview(img)
  }, [preview])

  return (
    <div
      ref={drag}
      className={`
        flex items-center space-x-3 p-3 rounded-lg border-2 border-dashed cursor-move
        transition-all duration-200 hover:shadow-md
        ${isDragging ? 'opacity-50' : 'opacity-100'}
        ${isDragging ? 'border-blue-400' : 'border-gray-300'}
      `}
      style={{ backgroundColor: color + '20' }}
    >
      <div 
        className="w-8 h-8 rounded-full flex items-center justify-center text-white"
        style={{ backgroundColor: color }}
      >
        <Icon size={16} />
      </div>
      <div>
        <div className="font-medium text-sm text-gray-900">{label}</div>
        <div className="text-xs text-gray-500 capitalize">{type}</div>
      </div>
    </div>
  )
}

const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-base font-semibold text-gray-900 hover:text-gray-700 transition-colors"
      >
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>{title}</span>
      </button>
      
      {isOpen && (
        <div className="space-y-2 pl-5">
          {children}
        </div>
      )}
    </div>
  )
}

const FieldOption = ({ type, icon: Icon, label, description, isSelected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full p-2 rounded-lg border-2 transition-all duration-200 text-left
        ${isSelected 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
        }
      `}
    >
      <div className="flex items-center space-x-2">
        <div 
          className={`
            w-6 h-6 rounded flex items-center justify-center
            ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}
          `}
        >
          <Icon size={14} />
        </div>
        <div>
          <div className={`font-medium text-sm ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
            {label}
          </div>
          <div className={`text-xs ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
            {description}
          </div>
        </div>
      </div>
    </button>
  )
}

const TeamPalette = () => {
  const { 
    fieldSettings, 
    updateFieldSettings, 
    teams, 
    props, 
    animations, 
    clearTeams, 
    clearProps, 
    clearAnimations, 
    setPlaying, 
    setCurrentTime,
    resetPlayName,
    clearSelectedEntity
  } = usePlayStore()
  
  const handleFieldTypeChange = (newFieldType) => {
    // Check if there are any existing teams, props, or animations
    const hasContent = teams.length > 0 || props.length > 0 || animations.length > 0
    
    if (hasContent) {
      // Show confirmation dialog
      const confirmed = confirm(
        'Switching field types will clear all teams, props, and animations. Are you sure you want to continue?'
      )
      
      if (!confirmed) {
        return // User cancelled, don't change field type
      }
      
      // Clear everything
      clearSelectedEntity() // Clear any selected entity highlight first
      // Small delay to ensure visual update happens before clearing sprites
      setTimeout(() => {
        clearTeams()
        clearProps()
        clearAnimations()
        setPlaying(false)
        setCurrentTime(0)
        resetPlayName()  // Reset play name to "Untitled Play"
      }, 10)
    }
    
    // Update field type
    updateFieldSettings({ fieldType: newFieldType })
  }
  
  const fieldTypes = [
    {
      type: 'full',
      icon: Square,
      label: 'Full Field',
      description: 'Complete soccer field with all markings'
    },
    {
      type: 'half',
      icon: RectangleHorizontal,
      label: 'Half Field',
      description: 'Half soccer field in landscape orientation'
    },
    {
      type: 'plain',
      icon: Square,
      label: 'Plain Field',
      description: 'Simple green field with no markings'
    }
  ]

  const teamTypes = [
    {
      type: 'red-team',
      icon: Shield,
      color: '#EF4444',
      label: 'Red Team'
    },
    {
      type: 'blue-team',
      icon: User,
      color: '#3B82F6',
      label: 'Blue Team'
    },
    {
      type: 'green-team',
      icon: Zap,
      color: '#10B981',
      label: 'Green Team'
    },
    {
      type: 'orange-team',
      icon: Target,
      color: '#F59E0B',
      label: 'Orange Team'
    }
  ]

  const propTypes = [
    {
      type: 'cone',
      icon: Cone,
      color: '#F97316', // Orange
      label: 'Cone',
      dragType: 'prop'
    },
    {
      type: 'soccer-ball',
      icon: Circle,
      color: '#000000', // Black
      label: 'Soccer Ball',
      dragType: 'prop'
    }
  ]

  return (
    <div className="space-y-4">
      {/* Custom drag layer for enhanced drag previews */}
      <CustomDragLayer />
      
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Field Elements</h3>
        <p className="text-sm text-gray-600">
          Drag elements onto the field to create your play
        </p>
      </div>
      
      <CollapsibleSection title="Fields" defaultOpen={true}>
        <div className="space-y-2">
          {fieldTypes.map((fieldType) => (
            <FieldOption
              key={fieldType.type}
              type={fieldType.type}
              icon={fieldType.icon}
              label={fieldType.label}
              description={fieldType.description}
              isSelected={fieldSettings.fieldType === fieldType.type}
              onClick={() => handleFieldTypeChange(fieldType.type)}
            />
          ))}
        </div>
      </CollapsibleSection>
      
      <CollapsibleSection title="Teams" defaultOpen={true}>
        <div className="space-y-3">
          {teamTypes.map((teamType) => (
            <DraggableItem
              key={teamType.type}
              type={teamType.type}
              icon={teamType.icon}
              color={teamType.color}
              label={teamType.label}
              dragType="team"
            />
          ))}
        </div>
      </CollapsibleSection>
      
      <CollapsibleSection title="Props" defaultOpen={true}>
        <div className="space-y-3">
          {propTypes.map((propType) => (
            <DraggableItem
              key={propType.type}
              type={propType.type}
              icon={propType.icon}
              color={propType.color}
              label={propType.label}
              dragType="prop"
            />
          ))}
        </div>
      </CollapsibleSection>
      
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Instructions</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Drag teams and props to the field</li>
          <li>• Click and drag to move elements</li>
          <li>• Right-click to remove elements</li>
          <li>• All props can be moved and repositioned</li>
          <li>• Use Animation Editor to animate your play</li>          
        </ul>
      </div>
    </div>
  )
}

export default TeamPalette
